var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useEffect, useMemo, useState } from 'react';
import FormError from '../partials/FormError';
import __ from '../../helpers/translation';
import { Combobox } from '../core';
import { useAsync } from 'react-use';
import apiRoutes from '@/actions/api';
import { z } from 'zod';
/**
 * Mapping of form input fields to their corresponding data properties
 * @constant {Object.<string, string>} INPUT_MAP
 * @property {string} name - Location name field mapping
 * @property {string} address - Location address field mapping
 * @property {string} city - Location city field mapping
 * @property {string} zipcode - Location zipcode field mapping
 * @property {string} contact_person - Contact person field mapping
 * @property {string} departament - Department field mapping
 * @property {string} report_email - Report email field mapping
 * @property {string} user_can_modify - User permission for location modification
 * @property {string} user_can_send_email - User permission for sending emails
 * @property {string} user_can_create_sublocations - User permission for creating sub-locations
 * @property {string} user_can_update_sublocations - User permission for updating sub-locations
 */
var INPUT_MAP = {
    'name': 'name',
    'address': 'address',
    'city': 'city',
    'zipcode': 'zipcode',
    'contact_person': 'contact_person',
    'departament': 'departament',
    'report_email': 'report_email',
    'project_number': 'project_number',
    'user_can_modify': 'can_modify',
    'user_can_send_email': 'can_send_email',
    'user_can_create_sublocations': 'can_create_sublocations',
    'user_can_update_sublocations': 'can_update_sublocations',
};
var locationWorkerSchema = z.object({
    worker_id: z.coerce.number().int().min(0),
    user_id: z.coerce.number().int().min(0),
    certificate_id: z.string().min(1).nullable(),
    company_name: z.string().min(1).nullable(),
    user_full_name: z.string().min(1).nullable(),
    user_email: z.string().email(),
}).array();
var LocationModal = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h;
    var location = _a.location, callback = _a.callback, previewMode = _a.previewMode, modal = _a.modal, companyId = _a.companyId;
    /**
     * Memoized modal title based on location and preview mode state.
     * @returns {string} Localized title string that can be:
     * - Add location title when no location is present
     * - Preview location title with location name when in preview mode
     * - Edit location title with location name otherwise
     */
    var modalTitle = useMemo(function () {
        var _a, _b;
        if (!location) {
            return __('app.inspections.add_location_title');
        }
        if (previewMode) {
            return __('app.inspections.preview_location_title', { location: (_a = location.name) !== null && _a !== void 0 ? _a : '' });
        }
        return __('app.inspections.edit_location_title', { location: (_b = location.name) !== null && _b !== void 0 ? _b : '' });
    }, [location, previewMode]);
    var locationWorkers = useAsync(function () { return __awaiter(void 0, void 0, void 0, function () {
        var endpoint, response, parsed, err_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!companyId)
                        return [2 /*return*/, []];
                    endpoint = apiRoutes.companies.workers.replace(':company', companyId.toString());
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 4, , 5]);
                    return [4 /*yield*/, window.axios.get(endpoint)];
                case 2:
                    response = _a.sent();
                    return [4 /*yield*/, locationWorkerSchema.spa(response.data)];
                case 3:
                    parsed = _a.sent();
                    if (!parsed.success) {
                        console.error(parsed.error);
                        return [2 /*return*/, []];
                    }
                    return [2 /*return*/, parsed.data];
                case 4:
                    err_1 = _a.sent();
                    console.error(err_1);
                    return [2 /*return*/, []];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [companyId]).value;
    var _j = useState([]), selectedWorkerIds = _j[0], setSelectedWorkerIds = _j[1];
    useAsync(function () { return __awaiter(void 0, void 0, void 0, function () {
        var endpoint, responseSchema, response, parsed, err_2;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!(location === null || location === void 0 ? void 0 : location.id))
                        return [2 /*return*/];
                    if (!locationWorkers || locationWorkers.length === 0)
                        return [2 /*return*/];
                    endpoint = apiRoutes.locations.workers.replace(':location', (_a = location === null || location === void 0 ? void 0 : location.id.toString()) !== null && _a !== void 0 ? _a : '');
                    responseSchema = z.object({
                        workerIds: z.array(z.coerce.number().int().min(0))
                    });
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 4, , 5]);
                    return [4 /*yield*/, window.axios.get(endpoint)];
                case 2:
                    response = _b.sent();
                    return [4 /*yield*/, responseSchema.spa(response.data)];
                case 3:
                    parsed = _b.sent();
                    if (!parsed.success) {
                        console.error(parsed.error);
                        return [2 /*return*/];
                    }
                    setSelectedWorkerIds(parsed.data.workerIds);
                    return [3 /*break*/, 5];
                case 4:
                    err_2 = _b.sent();
                    console.error(err_2);
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [locationWorkers, location]);
    // JQuery prefill (kept for compatibility with old code)
    useEffect(function () {
        var form = window.$('#modalAddLocation form');
        var inputs = form.find('input');
        if (!location) {
            inputs.val('');
            return;
        }
        window.$.each(inputs, function (index) {
            var input = window.$(inputs[index]);
            var name = input.attr('name');
            if (input.attr('type') == 'checkbox') {
                input.prop('checked', location[INPUT_MAP[name]]);
            }
            else {
                input.val(location[INPUT_MAP[name]]);
            }
        });
    }, [location]);
    return (React.createElement("div", { className: "modal fade", id: "modalAddLocation", tabIndex: -1, role: "dialog", "aria-labelledby": "modalAddLocation", "aria-hidden": "true" },
        React.createElement("div", { className: "modal-dialog modal-lg modal-dialog-centered", role: "document" },
            React.createElement("div", { className: "modal-content p-3 p-sm-5" },
                React.createElement("div", { className: "modal-header" },
                    React.createElement("h1", { className: "modal-title fw-600", id: "exampleModalLongTitle" }, modalTitle),
                    React.createElement("button", { type: "button", className: "close", "data-dismiss": "modal", "aria-label": "Close" },
                        React.createElement("span", { "aria-hidden": "true" }, "\u00D7"))),
                React.createElement("div", { className: "modal-body" },
                    (modal === null || modal === void 0 ? void 0 : modal.errors) && (React.createElement(FormError, null, modal.errors)),
                    previewMode && location ? (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: "form-group" },
                            React.createElement("label", { className: "font-weight-bold", htmlFor: "name" }, __('app.inspections.add_location_input_name')),
                            React.createElement("div", null, location.name)),
                        React.createElement("div", { className: "form-group" },
                            React.createElement("label", { className: "font-weight-bold", htmlFor: "name" }, __('app.inspections.add_location_input_address')),
                            React.createElement("div", null, location.address)),
                        React.createElement("div", { className: "form-group" },
                            React.createElement("label", { className: "font-weight-bold", htmlFor: "name" }, __('app.inspections.add_location_input_city')),
                            React.createElement("div", null, location.city)),
                        React.createElement("div", { className: "form-group" },
                            React.createElement("label", { className: "font-weight-bold", htmlFor: "name" }, __('app.inspections.add_location_input_zipcode')),
                            React.createElement("div", null, location.zipcode)),
                        React.createElement("div", { className: "form-group" },
                            React.createElement("label", { className: "font-weight-bold", htmlFor: "name" }, __('app.inspections.add_location_input_contact_person')),
                            React.createElement("div", null, location.contact_person)),
                        React.createElement("div", { className: "form-group" },
                            React.createElement("label", { className: "font-weight-bold", htmlFor: "name" }, __('app.inspections.add_location_input_departament')),
                            React.createElement("div", null, location.departament)),
                        React.createElement("div", { className: "form-group" },
                            React.createElement("label", { className: "font-weight-bold", htmlFor: "name" }, __('app.inspections.add_location_input_project_number')),
                            React.createElement("div", null, location.project_number)))) : (React.createElement("form", { onSubmit: function (e) { callback(e); setSelectedWorkerIds([]); } },
                        React.createElement("div", { className: "form-group" },
                            React.createElement("div", { className: "form-label-group" },
                                React.createElement("input", { name: "name", defaultValue: location ? location.name : '', type: "text", className: "form-control", placeholder: __('app.inspections.add_location_input_name') }),
                                React.createElement("label", { htmlFor: "name" }, __('app.inspections.add_location_input_name')))),
                        React.createElement("div", { className: "form-group" },
                            React.createElement("div", { className: "form-label-group" },
                                React.createElement("input", { name: "address", defaultValue: (_b = location === null || location === void 0 ? void 0 : location.address) !== null && _b !== void 0 ? _b : '', type: "text", className: "form-control", placeholder: __('app.inspections.add_location_input_address') }),
                                React.createElement("label", { htmlFor: "name" }, __('app.inspections.add_location_input_address')))),
                        React.createElement("div", { className: "form-group" },
                            React.createElement("div", { className: "form-label-group" },
                                React.createElement("input", { name: "city", defaultValue: (_c = location === null || location === void 0 ? void 0 : location.city) !== null && _c !== void 0 ? _c : '', type: "text", className: "form-control", placeholder: __('app.inspections.add_location_input_city') }),
                                React.createElement("label", { htmlFor: "name" }, __('app.inspections.add_location_input_city')))),
                        React.createElement("div", { className: "form-group" },
                            React.createElement("div", { className: "form-label-group" },
                                React.createElement("input", { name: "zipcode", defaultValue: (_d = location === null || location === void 0 ? void 0 : location.zipcode) !== null && _d !== void 0 ? _d : '', type: "text", className: "form-control", placeholder: __('app.inspections.add_location_input_zipcode') }),
                                React.createElement("label", { htmlFor: "name" }, __('app.inspections.add_location_input_zipcode')))),
                        React.createElement("div", { className: "form-group" },
                            React.createElement("div", { className: "form-label-group" },
                                React.createElement("input", { name: "contact_person", defaultValue: (_e = location === null || location === void 0 ? void 0 : location.contact_person) !== null && _e !== void 0 ? _e : '', type: "text", className: "form-control", placeholder: __('app.inspections.add_location_input_contact_person') }),
                                React.createElement("label", { htmlFor: "name" }, __('app.inspections.add_location_input_contact_person')))),
                        React.createElement("div", { className: "form-group" },
                            React.createElement("div", { className: "form-label-group" },
                                React.createElement("input", { name: "departament", defaultValue: (_f = location === null || location === void 0 ? void 0 : location.departament) !== null && _f !== void 0 ? _f : '', type: "text", className: "form-control", placeholder: __('app.inspections.add_location_input_departament') }),
                                React.createElement("label", { htmlFor: "name" }, __('app.inspections.add_location_input_departament')))),
                        React.createElement("div", { className: "form-group" },
                            React.createElement("div", { className: "form-label-group" },
                                React.createElement("input", { name: "project_number", defaultValue: (_g = location === null || location === void 0 ? void 0 : location.project_number) !== null && _g !== void 0 ? _g : '', type: "text", className: "form-control", placeholder: __('app.inspections.add_location_input_project_number') }),
                                React.createElement("label", { htmlFor: "name" }, __('app.inspections.add_location_input_project_number')))),
                        React.createElement("div", { className: "form-group" },
                            React.createElement("div", { className: "form-label-group" },
                                React.createElement("input", { name: "report_email", defaultValue: (_h = location === null || location === void 0 ? void 0 : location.report_email) !== null && _h !== void 0 ? _h : '', type: "text", className: "form-control", maxLength: 191, placeholder: __('app.inspections.add_location_input_report_email') }),
                                React.createElement("label", { htmlFor: "name" }, __('app.inspections.add_location_input_report_email')))),
                        React.createElement("div", { className: "form-group" },
                            React.createElement(Combobox, { searchValues: (locationWorkers || []).map(function (worker) {
                                    var _a;
                                    return ({
                                        displayName: "".concat((_a = worker.user_full_name) !== null && _a !== void 0 ? _a : worker.user_email, " ").concat(worker.company_name ? "- ".concat(worker.company_name) : ''),
                                        value: worker.worker_id,
                                    });
                                }), selectedItems: selectedWorkerIds
                                    .map(function (workerId) { return (locationWorkers || []).find(function (worker) { return worker.worker_id === workerId; }); })
                                    .filter(function (worker) { return worker !== undefined; })
                                    .map(function (worker) {
                                    var _a;
                                    return ({
                                        displayName: (_a = worker === null || worker === void 0 ? void 0 : worker.user_full_name) !== null && _a !== void 0 ? _a : worker === null || worker === void 0 ? void 0 : worker.user_email,
                                        value: worker === null || worker === void 0 ? void 0 : worker.worker_id,
                                    });
                                }), setSelectedValues: function (selectedIds) {
                                    setSelectedWorkerIds(selectedIds);
                                }, placeholder: __('app.inspections.add_location_input_select_workers') }),
                            React.createElement("input", { type: "text", name: "worker_ids", readOnly: true, value: selectedWorkerIds.join(','), style: { display: 'none' } })),
                        React.createElement("button", __assign({ type: "submit", className: "btn btn-success d-block w-100 mt-5" }, modal && modal.loading && { 'disabled': true }),
                            __('app.general.save'),
                            modal && modal.loading && (React.createElement("i", { className: "far fa-circle-notch fa-spin ml-3" }))))))))));
};
export default LocationModal;
